import { CONTRACT, useContract } from '@apeiron/contract'
import { CurrencyTokenType } from '@apeiron/currency'
import {
  CustomError,
  useAccountContext,
  useChainSwitcher,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { useMktErrorHandling } from '@apeiron/wallet'
import { ERROR_CODE } from '@src/constants/error'
import useAllowCurrency from '@src/hooks/contract/useAllowCurrency'
import {
  MarketplaceBuyParams,
  MarketplaceCancelSellParams,
  MarketplaceSellParams
} from '@src/hooks/contract/useMarketplace'
import { getTime } from 'date-fns'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Address, isAddress } from 'viem'

const approvalError = new CustomError(
  'user reject to approve token for operation',
  ERROR_CODE.CONTRACT.APPROVAL_FAIL
)

const verifyRetryError = new CustomError(
  'verify exceed retry limit',
  ERROR_CODE.CONTRACT.WAIT_SEND_OPERATION_RESULT_TIMEOUT
)

const usePolygonMarketplace = (): Hook => {
  const marketplaceContract = useContract()

  const { account } = useAccountContext()

  const { web3Utils } = useWeb3()

  const { walletAddress } = account

  const { showErrorPopup } = useMktErrorHandling()

  const messageContext = useMessageContext()

  const { getContractAddress } = useChainSwitcher()

  const { t } = useTranslation()

  const { requestCurrency } = useAllowCurrency()

  const handleError = useCallback(
    async (exec: () => Promise<void>) => {
      try {
        await exec()

        return true
      } catch (e) {
        showErrorPopup(e)

        return false
      }
    },
    [showErrorPopup]
  )

  const createListing = useCallback(
    async (params: MarketplaceSellParams): Promise<boolean> => {
      const { contractParams, approve, verify } = params

      const {
        duration,
        endPrice,
        erc1155,
        nftAddress,
        nftId,
        startPrice,
        tokenAddress
      } = contractParams

      const isSuccess: boolean = await handleError(async () => {
        if (erc1155) {
          const { quantity } = erc1155

          const contract1155 = CONTRACT.MARKETPLACE_1155.CREATE_LISTING

          const contractAddress = getContractAddress(contract1155.address)

          await approve(contractAddress)
          await marketplaceContract.sendWithOption(
            { contract: contract1155 },
            nftAddress,
            nftId,
            quantity,
            tokenAddress,
            startPrice,
            endPrice,
            duration
          )
        } else {
          const contract721 = CONTRACT.MARKETPLACE_721.CREATE_LISTING

          const contractAddress = getContractAddress(contract721.address)

          await approve(contractAddress)
          await marketplaceContract.sendWithOption(
            { contract: contract721 },
            nftAddress,
            nftId,
            tokenAddress,
            startPrice,
            endPrice,
            duration
          )
        }

        const success = await verify(nftId)

        if (!success) {
          throw verifyRetryError
        }
      })

      return isSuccess
    },
    [marketplaceContract, handleError, getContractAddress]
  )

  const updateListing = useCallback(
    async (params: MarketplaceSellParams): Promise<boolean> => {
      const { contractParams, approve, verify } = params

      const {
        duration,
        endPrice,
        nftAddress,
        nftId,
        startPrice,
        tokenAddress,
        erc1155
      } = contractParams

      const isSuccess: boolean = await handleError(async () => {
        if (erc1155) {
          const { index } = erc1155

          const contract1155 = CONTRACT.MARKETPLACE_1155.UPDATE_LISTING

          const contractAddress = getContractAddress(contract1155.address)

          await approve(contractAddress)
          await marketplaceContract.sendWithOption(
            { contract: contract1155 },
            nftAddress,
            nftId,
            index,
            tokenAddress,
            startPrice,
            endPrice,
            duration
          )
        } else {
          const contract721 = CONTRACT.MARKETPLACE_721.UPDATE_LISTING

          const contractAddress = getContractAddress(contract721.address)

          await approve(contractAddress)
          await marketplaceContract.sendWithOption(
            { contract: contract721 },
            nftAddress,
            nftId,
            tokenAddress,
            startPrice,
            endPrice,
            duration
          )
        }

        const success = await verify(nftId)

        if (!success) {
          throw verifyRetryError
        }
      })

      return isSuccess
    },
    [marketplaceContract, handleError, getContractAddress]
  )

  const cancelListing = useCallback(
    async (params: MarketplaceCancelSellParams): Promise<boolean> => {
      const isSuccess: boolean = await handleError(async () => {
        const { contractParams, verify } = params

        const { nftAddress, nftId, erc1155 } = contractParams

        messageContext.setInfo({
          title: t('dialog.cancel_sell.cancelling'),
          message: t('common.popup.transaction.be_patient')
        })

        if (erc1155) {
          const { listIndex } = erc1155

          const contract1155 = CONTRACT.MARKETPLACE_1155.CANCEL_LISTING

          await marketplaceContract.sendWithOption(
            { contract: contract1155 },
            nftAddress,
            nftId,
            listIndex
          )
        } else {
          const contract721 = CONTRACT.MARKETPLACE_721.CANCEL_LISTING

          await marketplaceContract.sendWithOption(
            { contract: contract721 },
            nftAddress,
            nftId
          )
        }

        const success = verify ? await verify(nftId) : true

        if (!success) {
          throw verifyRetryError
        }
      })

      messageContext.setInfo(null)

      return isSuccess
    },
    [marketplaceContract, messageContext, handleError, t]
  )

  const requestAllowance = useCallback(
    async (
      ownerAddress: string,
      contractAddress: Address,
      settlePrice: bigint,
      settleOrderTokenType: CurrencyTokenType = CurrencyTokenType.Weth
    ): Promise<boolean> => {
      if (!isAddress(ownerAddress)) {
        return false
      }

      switch (settleOrderTokenType) {
        case CurrencyTokenType.Weth:
        case CurrencyTokenType.Usdc:
        case CurrencyTokenType.Anima:
        case CurrencyTokenType.Aprs: {
          return requestCurrency(
            ownerAddress,
            contractAddress,
            settleOrderTokenType,
            settlePrice
          )
        }

        default:
          return false
      }
    },
    [requestCurrency]
  )

  const buy = useCallback(
    async (params: MarketplaceBuyParams): Promise<boolean> => {
      const { contractParams, verify, onApproved, onBought, onFailed } = params

      const { price, listAt, nftAddress, nftId, erc1155 } = contractParams

      const isSuccess: boolean = await handleError(async () => {
        const listTimestamp = getTime(new Date(listAt)) / 1000

        if (erc1155) {
          const { listIndex, sellerAddress, quantity } = erc1155

          const contract1155 = CONTRACT.MARKETPLACE_1155.BUY

          const contractAddress = getContractAddress(contract1155.address)

          const approved = await requestAllowance(
            walletAddress,
            contractAddress,
            BigInt(web3Utils.toWei(price.toString()))
          )

          if (!approved) {
            throw approvalError
          }

          onApproved()

          const listTimestamp = getTime(new Date(listAt)) / 1000

          await marketplaceContract.sendWithOption(
            { contract: contract1155 },
            sellerAddress,
            nftAddress,
            nftId,
            quantity,
            listIndex,
            listTimestamp
          )

          const success = await verify(nftId)

          if (!success) {
            throw verifyRetryError
          }

          onBought()
        } else {
          const contract721 = CONTRACT.MARKETPLACE_721.BUY

          const contractAddress = getContractAddress(contract721.address)

          const approved = await requestAllowance(
            walletAddress,
            contractAddress,
            BigInt(web3Utils.toWei(price.toString())),
            params.contractParams.settleOrderTokenType
          )

          if (!approved) {
            throw approvalError
          }

          onApproved()

          await marketplaceContract.sendWithOption(
            { contract: contract721 },
            nftAddress,
            nftId,
            listTimestamp
          )

          const success = await verify(nftId)

          if (!success) {
            throw verifyRetryError
          }

          onBought()
        }
      }).catch(err => {
        onFailed()

        throw err
      })

      if (!isSuccess) {
        onFailed()
      }

      return isSuccess
    },
    [
      handleError,
      getContractAddress,
      walletAddress,
      web3Utils,
      marketplaceContract
    ]
  )

  return { buy, cancelListing, createListing, updateListing }
}

type Hook = {
  buy: (params: MarketplaceBuyParams) => Promise<boolean>
  cancelListing: (params: MarketplaceCancelSellParams) => Promise<boolean>
  createListing: (params: MarketplaceSellParams) => Promise<boolean>
  updateListing: (params: MarketplaceSellParams) => Promise<boolean>
}

export default usePolygonMarketplace
